/* eslint arrow-body-style: */
import jwtDecode from 'jwt-decode';
import { SubmissionError } from 'redux-form';
import has from 'lodash.has';
import { getCancelTokenSource, isCancel, saveToken, removeToken } from 'api/Helpers';
import { AUTH_TOKEN } from 'api/Helpers/constants';
import { SHOPPING_CART, WISH_LIST } from 'redux/domain/Cart/constants';
import { actions as cartActions } from 'redux/domain/Cart';
import config from 'shared/config';
import {
	getGuestCustomer as apiGetGuestCustomer,
	login as loginAsync,
	register as registerAsync,
} from 'api/User';
import {
	LOGIN_SUCCESS,
	LOGIN_INVALID_EMAIL_OR_PASS,
	LOGIN_NOT_ACTIVE,
	LOGIN_DELETED,
	LOGIN_NOT_REGISTERED,
	REGISTER_SUCCESS,
	REGISTER_CUSTOMER_ALREADY_EXISTS,
	REGISTER_UNKNOWN_ERROR,
} from 'api/User/statusCodes';
import Customer from 'models/Customer';

import {
	IS_LOGGING_IN,
	IS_REGISTERING,
	LAST_ACTIVITY,
	LOGIN,
	LOGIN_ERROR,
	LOGOUT,
	REGISTER_ERROR,
} from './constants';

/**
 * Clear any saved login attempt error
 */
const clearLoginError = () => ({
	type: LOGIN_ERROR,
	loginError: undefined,
});

/**
 * Clear any saved registration attempt error
 */
const clearRegisterError = () => ({
	type: REGISTER_ERROR,
	registerError: undefined,
});

/**
 * Save a customer to the store.
 * @param {Customer} customer
 */
const login = customer => ({
	type: LOGIN,
	customer,
});

/**
 * Discard the logged in customer and replace with a new guest customer.
 */
const resetUser = () => ({
	type: LOGOUT,
});

/**
 * Indicate that a login attempt is in progress or has finished.
 */
const updateLoginStatus = isLoggingIn => ({
	type: IS_LOGGING_IN,
	isLoggingIn,
});

/**
 * Indicate that a registration attempt is in progress or has finished.
 * @param {bool} isRegistering
 */
const updateRegisterStatus = isRegistering => ({
	type: IS_REGISTERING,
	isRegistering,
});

/**
 * Update the the user's last activity time with the passed in time.
 * @param {number} lastActivityTime javascript time
 */
const updateLastActivity = lastActivityTime => {
	return {
		type: LAST_ACTIVITY,
		lastActivity: lastActivityTime,
	};
};

/**
 * Indicate that an error occurred during login.
 * @param {Error} loginError
 */
const updateLoginError = loginError => ({
	type: LOGIN_ERROR,
	loginError,
});

/**
 * Indicate that an error occurred during registration.
 * @param {Error} registerError
 */
const updateRegisterError = registerError => ({
	type: REGISTER_ERROR,
	registerError,
});

/**
 * Get a guest customer id to anonymously save a shopping cart and/or wishlist.
 * @param {axios cancel token} cancelToken
 */
const getGuestCustomer = cancelToken => {
	return (dispatch, getState) => {
		// If we already have a customer with an id then we do not need to refetch a new guest.
		const { user } = getState();
		if (user.get('customer').id != null) {
			return false;
		}

		return apiGetGuestCustomer(cancelToken)
			.then(response => {
				const customer = new Customer();
				try {
					const decodedToken = jwtDecode(response.data.authToken);
					customer.id = parseInt(decodedToken.customerId, 10);
					saveToken(response.data.authToken, AUTH_TOKEN);
					dispatch(login(customer));
				} catch (ex) {
					// jwtDecode throws an error on malformed tokens.
					dispatch(
						updateLoginError(
							new Error('Invalid token returned while attempting to create new guest user.'),
						),
					);
				}
			})
			.catch(error => {
				if (isCancel(error)) {
					// Request was canceled.
					return;
				}
				dispatch(updateLoginError(error));
			});
	};
};

/**
 * Log into the eSTORIS database.
 * See the api/User module for more information.
 * Errors (network) will update LoginError
 * Dispatched from the redux-form submit method. It's weird to throw an exception out of a promise but the SubmissionError tells redux-form
 * to display an error message under the specified field. Throwing an error in a promise causes it to reject which is why we need to rethrow
 * in the catch.
 * @param {Axios cancel token} cancelToken
 * @param {string} loginId
 * @param {string} password
 * Default errors are set as password errors because that field is the last field on the form.
 * The endpoint it's hitting is login but I already used the login for another action hence loginUser
 */
const loginUser = (cancelToken, email, password) => {
	return dispatch => {
		dispatch(updateLoginStatus(true));
		return loginAsync(cancelToken, email, password)
			.then(response => {
				dispatch(updateLoginStatus(false));
				switch (response.data.status) {
					case LOGIN_SUCCESS: {
						let customerId;
						try {
							const decodedToken = jwtDecode(response.data.authToken);
							saveToken(response.data.authToken);
							customerId = parseInt(decodedToken.customerId, 10);

							const loggedInCustomer = new Customer(customerId, response.data.name, false);
							loggedInCustomer.emailAddress = email;

							// update the Redux store and redirect the user back to where they came from.
							dispatch(login(loggedInCustomer));
						} catch (ex) {
							throw new SubmissionError({
								password: 'An error was encountered while attempting to login.',
							});
						}
						break;
					}
					case LOGIN_INVALID_EMAIL_OR_PASS: {
						throw new SubmissionError({
							email: 'Your email or password is incorrect',
						});
					}
					case LOGIN_NOT_ACTIVE:
					// intentional fall through to notregistered
					case LOGIN_DELETED:
					// intentional fall through to notregistered
					case LOGIN_NOT_REGISTERED: {
						throw new SubmissionError({
							email: 'Your account is no longer active',
						});
					}
					default: {
						throw new SubmissionError({
							password: 'An unknown error occurred while attempting to login.',
						});
					}
				}
			})
			.catch(error => {
				dispatch(updateLoginStatus(false));
				if (isCancel(error)) {
					// request was canceled.
					return;
				}

				if (error instanceof SubmissionError) {
					throw error;
				}

				dispatch(updateLoginError(error));
				// the real error is logged to the store.
				throw new SubmissionError({
					password: 'An error occurred while attempting to login.',
				});
			});
	};
};

/**
 * loginExernalUser
 * @param {*} jwtToken
 * Login user from external authentication
 * The jwtToken and user info are passed as parameters
 */
const loginExernalUser = (jwtToken, name, email) => {
	return dispatch => {
		let customerId;
		try {
			const decodedToken = jwtDecode(jwtToken);
			saveToken(jwtToken);
			customerId = parseInt(decodedToken.customerId, 10);

			const loggedInCustomer = new Customer(customerId, name, false);
			loggedInCustomer.emailAddress = email;

			// update the Redux store and redirect the user back to where they came from.
			dispatch(login(loggedInCustomer));
		} catch (ex) {
			throw new SubmissionError({
				password: 'An error was encountered while attempting to login.',
			});
		}
	};
};

/**
 * Perform any cleanup needed for a logout.
 * When guests time out back to the landing page we need to clear any cart/wishlist items
 * since NOP recycles guest users.
 */
const logout = () => {
	return (dispatch, getState) => {
		const { cart, user } = getState();
		const customer = user.get('customer');
		if (customer.isGuest) {
			const deleteRequests = [];
			const shoppingCart = cart.get('shoppingCart');
			const wishList = cart.get('wishList');
			if (shoppingCart.items.length !== 0) {
				const { token } = getCancelTokenSource();
				deleteRequests.push(dispatch(cartActions.deleteCart(token, customer.id, SHOPPING_CART)));
			}
			if (wishList.items.length !== 0) {
				const { token } = getCancelTokenSource();
				deleteRequests.push(dispatch(cartActions.deleteCart(token, customer.id, WISH_LIST)));
			}
			// wait for both requests to complete before destroying the token and resetting the user.
			return Promise.all(deleteRequests).then(() => {
				removeToken();
				dispatch(resetUser());
			});
		}

		// remove any saved authentication tokens from session storage
		// but do not destroy until we are done making the requests!
		removeToken();
		dispatch(resetUser());

		if (config.SingleSignOn.STORISSingleSignOnEnabled && !customer.isGuest) {
			window.location = `${
				config.ESTORIS_BASE_PATH
			}/STORISAuthentication/logout?returnUrl=Kiosk/Welcome`;
		}

		return null;
	};
};

/**
 * Register a new customer in the eSTORIS database.
 * See the api/User module for more information.
 * Errors (for example customer already exists or network) will update registerError
 * Dispatched from the redux-form submit method. It's weird to throw an exception out of a promise but the SubmissionError tells redux-form
 * to display an error message under the specified field. Throwing an error in a promise causes it to reject which is why we need to rethrow
 * in the catch.
 * @param {Axios cancel token} cancelToken
 * @param {string} email
 * @param {string} password
 * @param {string} firstName
 * @param {string} lastName
 * Default errors are set as password errors because that field is the last field on the form.
 */
const register = (cancelToken, email, password, firstName, lastName) => {
	return dispatch => {
		dispatch(updateRegisterStatus(true));

		return registerAsync(cancelToken, email, password, firstName, lastName)
			.then(response => {
				dispatch(updateRegisterStatus(false));

				// A 200 response will contain a status indicating whether the user was actually registered.
				const { status } = response.data;
				switch (status) {
					case REGISTER_SUCCESS: {
						const customer = new Customer();
						customer.emailAddress = email;
						customer.fullName = `${firstName} ${lastName}`;
						customer.isGuest = false;
						try {
							const decodedToken = jwtDecode(response.data.authToken);
							customer.id = parseInt(decodedToken.customerId, 10);
							saveToken(response.data.authToken, AUTH_TOKEN);
							dispatch(login(customer));
						} catch (ex) {
							// jwtDecode throws an error on malformed tokens.
							const errorMessage = 'Invalid token returned while attempting to register.';
							dispatch(updateRegisterError(new Error(errorMessage)));
							throw new SubmissionError({
								password: errorMessage,
							});
						}
						break;
					}
					case REGISTER_CUSTOMER_ALREADY_EXISTS: {
						throw new SubmissionError({
							email: 'A customer with this email already exists.',
						});
					}
					case REGISTER_UNKNOWN_ERROR:
					// intentional fall through
					default: {
						let errorMessage = 'Unknown error encountered while attempting to register.';
						if (has(response.data, 'message')) {
							errorMessage = response.data.message;
						}
						dispatch(updateRegisterError(new Error(errorMessage)));
						throw new SubmissionError({
							password: errorMessage,
						});
					}
				}
			})
			.catch(error => {
				dispatch(updateRegisterStatus(false));
				if (isCancel(error)) {
					// request was canceled.
					return;
				}

				if (error instanceof SubmissionError) {
					throw error;
				}

				dispatch(updateRegisterError(error));
				throw new SubmissionError({
					password: 'An error occurred while attempting to register.',
				});
			});
	};
};

export {
	clearLoginError,
	clearRegisterError,
	getGuestCustomer,
	login,
	loginExernalUser,
	loginUser,
	logout,
	register,
	resetUser,
	updateLastActivity,
	updateLoginError,
	updateLoginStatus,
	updateRegisterError,
	updateRegisterStatus,
};
